import i18n from 'i18next'
import { CatalogApartment } from '../../services/catalog'

// the catalog is English-only regardless of the app language
const tEn = (key: string) => i18n.getFixedT('en')(key)

const HIDDEN_VALUES = ['notSure', 'other', '']

/** English label of an apartment option, or undefined when there is nothing worth showing */
export const optionLabel = (group: string, value?: string): string | undefined => {
  if (!value || HIDDEN_VALUES.includes(value)) return undefined
  const key = `apartment.${group}.${value}`
  const label = tEn(key)
  return label && label !== key ? label : undefined
}

const euro = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 2,
  minimumFractionDigits: 0
})

export const formatEuro = (value?: number) => (value === undefined ? undefined : euro.format(value))

export const formatArea = (value?: number) =>
  value === undefined ? undefined : `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(value)} m²`

const dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

export const availability = (apartment: CatalogApartment) => {
  if (!apartment.availableFromDate) return 'By agreement'
  const from = new Date(apartment.availableFromDate)
  if (Number.isNaN(from.getTime())) return 'By agreement'
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  return from <= today ? 'Available now' : `From ${dateFormat.format(from)}`
}

export const formatDate = (iso?: string) => {
  if (!iso) return undefined
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? undefined : dateFormat.format(date)
}

/** Finnish room counts include the living room: 1h is a studio, 3h has 2 bedrooms */
export const bedroomCount = (rooms?: string): number | undefined => {
  if (rooms === '+5h') return 5
  const count = parseInt(rooms || '', 10)
  return count ? count - 1 : undefined
}

export const bedroomsLabel = (rooms?: string) => {
  const count = bedroomCount(rooms)
  if (count === undefined) return undefined
  if (count === 0) return 'Studio'
  if (rooms === '+5h') return '5+ Bedrooms'
  return `${count} ${count === 1 ? 'Bedroom' : 'Bedrooms'}`
}

const KITCHEN: Record<string, string> = {
  K: 'Kitchen',
  KK: 'Kitchenette',
  KT: 'Kitchen Area',
  AK: 'Open Kitchen',
  TUPAK: 'Eat-in Kitchen'
}
const SAUNA: Record<string, string> = { own: 'Sauna', shared: 'Shared Sauna' }
const BALCONY: Record<string, string> = {
  glass: 'Glazed Balcony',
  balconet: 'French Balcony',
  balcony: 'Balcony',
  protruding: 'Balcony'
}

/** English counterpart of the Finnish layout shorthand, e.g. "2 Bedrooms, Kitchen, Sauna, Glazed Balcony" */
export const layoutTitle = (apartment: CatalogApartment) => {
  const parts = [
    bedroomsLabel(apartment.rooms),
    apartment.kitchen && KITCHEN[apartment.kitchen],
    apartment.sauna && SAUNA[apartment.sauna],
    apartment.balcony && BALCONY[apartment.balcony],
    apartment.hasBuiltInCloset && 'Walk-in Closet'
  ].filter(Boolean)
  return parts.length ? parts.join(', ') : 'Apartment'
}

/** e.g. "2 Bedrooms · 48.5 m²"; the full layout is in the listing details */
export const listingTitle = (apartment: CatalogApartment) =>
  [bedroomsLabel(apartment.rooms) || 'Apartment', formatArea(apartment.squareMeters)].filter(Boolean).join(' · ')

export const locationLine = (apartment: CatalogApartment) =>
  [apartment.district, apartment.municipality].filter(Boolean).join(', ')

export const floorLabel = (apartment: CatalogApartment) => {
  if (apartment.floor === undefined) return undefined
  return apartment.floors ? `${apartment.floor} / ${apartment.floors}` : `${apartment.floor}`
}

export const depositLabel = (apartment: CatalogApartment) => {
  if (apartment.securityDeposit === 'other') return formatEuro(apartment.securityDepositAmount)
  if (apartment.securityDeposit === 'zero') return 'No deposit'
  const months: Record<string, number> = { oneMonth: 1, twoMonths: 2, threeMonths: 3 }
  const count = apartment.securityDeposit ? months[apartment.securityDeposit] : undefined
  if (!count) return formatEuro(apartment.securityDepositAmount)
  const amount = apartment.price ? ` (${formatEuro(apartment.price * count)})` : ''
  return `${count} ${count > 1 ? "months'" : "month's"} rent${amount}`
}

export const waterLabel = (apartment: CatalogApartment) => {
  const basis = apartment.waterFeeBasis
  if (basis === 'included') return 'Included in rent'
  const fee = formatEuro(apartment.waterFee)
  if (basis === 'perTenant') return fee ? `${fee} / person / month` : optionLabel('waterFeeBasis', basis)
  if (basis === 'perApartment') return fee ? `${fee} / month` : optionLabel('waterFeeBasis', basis)
  if (basis === 'metered') return fee ? `By consumption (approx. ${fee} / month)` : 'By consumption'
  if (basis === 'negotiate') return 'By agreement'
  return fee ? `${fee} / month` : undefined
}

export const energyLabel = (value?: string) => {
  if (!value) return undefined
  const rating = value.trim().match(/^([A-G])\s*\(?(20\d\d)?\)?$/)
  if (rating) return rating[2] ? `${rating[1]} (${rating[2]})` : rating[1]
  if (/ei lain|no energy/i.test(value)) return 'Not required by law'
  return value
}

export const featureList = (apartment: CatalogApartment): string[] =>
  [
    apartment.hasElevator && 'Lift',
    optionLabel('sauna', apartment.sauna === 'no' ? undefined : apartment.sauna),
    optionLabel('balcony', apartment.balcony === 'no' ? undefined : apartment.balcony),
    apartment.hasTerrace && 'Terrace',
    apartment.dishwasher === 'yes' && 'Dishwasher',
    apartment.laundryMachine === 'yes' && 'Washing machine',
    apartment.isFurnished && 'Furnished',
    apartment.internetIncluded && 'Internet included',
    apartment.hasLaundromat && 'Shared laundry room',
    apartment.petsAllowed === 'yes' && 'Pets allowed',
    apartment.groupsPossible && 'Suitable for flatmates'
  ].filter((item): item is string => !!item)
